import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { Card } from '@/components/layout';
import { usePageConfig } from '@/contexts/PageConfigContext';
import NotFoundPage from '@/pages/NotFoundPage';

interface InvitationPreview {
  id: number;
  tournamentName: string;
  tournamentDescription?: string;
  leagueName: string;
  inviterUsername: string;
  inviterFirstName: string;
  inviteeId?: number;
  participantsCount: number;
  expiresAt: string;
  invitationType: string;
}

const InvitationAcceptPage: React.FC = () => {
  const { inviteCode } = useParams<{ inviteCode: string }>();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('token');
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation(['tournament', 'common']);

  usePageConfig({ showHeader: false, showBottomNav: false, showNavigation: false });

  const [invitation, setInvitation] = useState<InvitationPreview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<any>(null);

  // Require either authentication or a valid token to view invite
  const hasAccess = !!(user || inviteToken);

  useEffect(() => {
    if (!hasAccess) {
      setIsLoading(false);
      return;
    }
    if (!inviteCode) return;

    const loadInvitationPreview = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await apiClient.getInvitationPreview(inviteCode);
        setInvitation(response.data.invite);
      } catch (error: any) {
        const errorMessage = error.response?.data?.error || 'Failed to load invitation';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    loadInvitationPreview();
  }, [inviteCode, hasAccess]);

  // Open the joined tournament after a short success notice; cleared when the
  // user leaves first ("go now", back), so it never replaces another entry.
  useEffect(() => {
    if (!success?.tournamentId) return;
    const timer = setTimeout(() => navigate(`/tournaments/${success.tournamentId}`, { replace: true }), 2000);
    return () => clearTimeout(timer);
  }, [success, navigate]);

  // No token and not logged in → show 404
  if (!isLoading && !hasAccess) {
    return <NotFoundPage />;
  }

  // Logged-in user opened invite meant for a different user → show 404
  if (!isLoading && user && invitation?.inviteeId && invitation.inviteeId !== user.id) {
    return <NotFoundPage />;
  }

  const redirectToLogin = (pendingAction: 'accept' | 'decline') => {
    navigate('/login', {
      state: { from: location, pendingAction },
      replace: true
    });
  };

  const handleAcceptInvitation = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (!inviteCode) {
      setError('Invalid invitation code');
      return;
    }

    // Not logged in → redirect to login, then come back to accept
    if (!user) {
      redirectToLogin('accept');
      return;
    }

    setIsAccepting(true);
    setError(null);

    try {
      const response = await apiClient.acceptInvitation(inviteCode);

      if (response.data) {
        setSuccess({
          message: 'Invitation accepted successfully!',
          tournamentName: response.data.tournamentName || invitation?.tournamentName,
          tournamentId: response.data.tournamentId || response.data.tournament_id
        });

      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to accept invitation';
      setError(errorMessage);
    } finally {
      setIsAccepting(false);
    }
  };

  const handleDeclineInvitation = async () => {
    if (!inviteCode) return;

    // Not logged in → redirect to login, then come back to decline
    if (!user) {
      redirectToLogin('decline');
      return;
    }

    setIsDeclining(true);
    setError(null);

    try {
      await apiClient.declineInvitation(inviteCode);
      navigate('/', { replace: true });
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to decline invitation';
      setError(errorMessage);
    } finally {
      setIsDeclining(false);
    }
  };

  const formatExpiryDate = (expiresAt: string): string => {
    const date = new Date(expiresAt);
    return date.toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="ls-flex ls-align-center ls-justify-center" style={{ minHeight: '80vh' }}>
        <Card className="ls-text-center" style={{ maxWidth: '400px', width: '100%' }}>
          <div className="ls-mb-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: 'var(--accent-primary)', margin: '0 auto' }}></div>
          </div>
          <p className="theme-text-secondary" style={{ fontSize: '13px' }}>{t('tournament:loadingInvitation')}</p>
        </Card>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="ls-flex ls-align-center ls-justify-center" style={{ minHeight: '80vh' }}>
        <Card className="ls-text-center" style={{ maxWidth: '400px', width: '100%' }}>
          <div className="ls-mb-4">
            <div
              className="ls-flex ls-align-center ls-justify-center"
              style={{
                width: '64px',
                height: '64px',
                background: 'var(--bg-error)',
                borderRadius: '50%',
                margin: '0 auto'
              }}
            >
              <svg className="theme-text-error" width="32" height="32" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
          </div>
          <h2 className="ls-h2 ls-mb-2">{t('tournament:invalidInvitation')}</h2>
          <p className="theme-text-secondary ls-mb-4" style={{ fontSize: '13px' }}>{error}</p>
          <button
            onClick={() => navigate('/')}
            className="btn btn--sm"
          >
            {t('tournament:goToHome')}
          </button>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="ls-flex ls-align-center ls-justify-center" style={{ minHeight: '80vh' }}>
          <Card className="ls-text-center" style={{ maxWidth: '400px', width: '100%' }}>
            <div className="ls-mb-4">
              <div
                className="ls-flex ls-align-center ls-justify-center"
                style={{
                  width: '64px',
                  height: '64px',
                  background: 'var(--bg-status-success)',
                  borderRadius: '50%',
                  margin: '0 auto'
                }}
              >
                <svg className="theme-status-success" width="32" height="32" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h2 className="ls-h2 ls-mb-2">{t('tournament:welcomeToTournament')}</h2>
            <p className="theme-text-secondary ls-mb-4" style={{ fontSize: '13px' }}>
              {t('tournament:successfullyJoined', { tournament: '' })}<strong>{success.tournamentName}</strong>
            </p>
            <p className="theme-text-muted ls-mb-4" style={{ fontSize: '11px' }}>
              {t('tournament:redirectingToTournament')}
            </p>
            <button
              onClick={() => navigate(`/tournaments/${success.tournamentId}`, { replace: true })}
              className="btn btn--sm"
            >
              {t('tournament:goToTournamentNow')}
            </button>
          </Card>
        </div>
    );
  }

  return (
    <div className="ls-flex ls-align-center ls-justify-center" style={{ minHeight: '80vh' }}>
        <Card style={{ maxWidth: '450px', width: '100%' }}>
          {/* Header */}
          <div className="ls-text-center ls-mb-4">
            <div style={{ fontSize: '48px' }} className="ls-mb-2">🏆</div>
            <h1 className="ls-h2 ls-mb-1">PClash</h1>
            <p className="theme-text-secondary" style={{ fontSize: '13px' }}>{t('tournament:tournamentInvitation')}</p>
          </div>

          {/* Tournament Info */}
          {invitation && (
            <div
              className="ls-py-4 ls-px-4 ls-mb-4"
              style={{
                background: 'linear-gradient(135deg, var(--accent-primary) 0%, var(--bg-button) 100%)',
                borderRadius: '12px',
                color: 'white'
              }}
            >
              <h2 className="ls-h3 ls-mb-2" style={{ color: 'white' }}>{invitation.tournamentName}</h2>
              {invitation.tournamentDescription && (
                <p className="ls-mb-3" style={{ fontSize: '13px', opacity: 0.9 }}>{invitation.tournamentDescription}</p>
              )}
              <div className="ls-grid ls-col-2 ls-gap-3" style={{ fontSize: '12px' }}>
                <div>
                  <span style={{ opacity: 0.75 }}>{t('tournament:competition')}</span>
                  <p className="ls-label" style={{ color: 'white' }}>{invitation.leagueName || 'Tournament'}</p>
                </div>
                <div>
                  <span style={{ opacity: 0.75 }}>{t('tournament:participantsCount')}</span>
                  <p className="ls-label" style={{ color: 'white' }}>{invitation.participantsCount}</p>
                </div>
              </div>
            </div>
          )}

          {/* Invitation Details */}
          {invitation && (
            <div className="theme-bg-tertiary ls-py-3 ls-px-3 ls-mb-4" style={{ borderRadius: '8px' }}>
              <p className="theme-text-secondary ls-mb-1" style={{ fontSize: '13px' }}>
                {t('tournament:invitedYou', { name: invitation.inviterFirstName || invitation.inviterUsername })}
              </p>
              <p className="theme-text-muted" style={{ fontSize: '11px' }}>
                {t('tournament:invitationExpires', { date: formatExpiryDate(invitation.expiresAt) })}
              </p>
            </div>
          )}

          {/* Accept Form */}
          <form onSubmit={handleAcceptInvitation} className="ls-flex ls-flex-col ls-gap-4">
            {!user && (
              <div className="theme-bg-tertiary ls-py-3 ls-px-3" style={{ borderRadius: '8px' }}>
                <p className="theme-text-secondary" style={{ fontSize: '13px' }}>
                  {t('tournament:loginRequiredForInvitation')}
                </p>
              </div>
            )}

            {user && (
              <div className="theme-bg-status-success ls-py-3 ls-px-3" style={{ borderRadius: '8px' }}>
                <p className="theme-text-link" style={{ fontSize: '13px' }}>
                  {t('tournament:loggedInAs', { name: user.first_name || user.username || user.email })}
                </p>
              </div>
            )}

            {error && (
              <div className="theme-bg-error ls-py-3 ls-px-3" style={{ borderRadius: '8px' }}>
                <p className="theme-text-error" style={{ fontSize: '13px' }}>{error}</p>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                type="button"
                onClick={handleDeclineInvitation}
                disabled={isDeclining}
                className="btn btn--sm btn--outlined"
              >
                {isDeclining ? t('tournament:declining') : t('tournament:decline')}
              </button>
              <button
                type="submit"
                disabled={isAccepting}
                className="btn btn--sm"
              >
                {isAccepting ? t('tournament:accepting') : t('tournament:accept')}
              </button>
            </div>
          </form>

          {/* Features */}
          <div className="theme-bg-tertiary ls-py-3 ls-px-3 ls-mt-4" style={{ borderRadius: '8px' }}>
            <h3 className="ls-label theme-text-link ls-mb-2">{t('tournament:whatYouGet')}</h3>
            <ul className="theme-text-secondary" style={{ fontSize: '12px', listStyle: 'none', padding: 0 }}>
              <li className="ls-mb-1">🎲 {t('tournament:featurePredictions')}</li>
              <li className="ls-mb-1">📊 {t('tournament:featurePoints')}</li>
              <li className="ls-mb-1">🏆 {t('tournament:featureCompete')}</li>
              <li className="ls-mb-1">📱 {t('tournament:featureAccess')}</li>
              <li>🔔 {t('tournament:featureNotifications')}</li>
            </ul>
          </div>
        </Card>
      </div>
  );
};

export default InvitationAcceptPage;
